/**
 * SoleadLab 2.0 - Data Stream Service
 *
 * Manages the real-time data connection to the monitoring backend.
 *
 * Primary: WebSocket (Hibernatable WS via Durable Object)
 * Fallback: Polling with exponential backoff when WS drops
 *
 * Features:
 * - WebSocket auto-reconnect with exponential backoff + jitter
 * - Snapshot hydration on connect/reconnect (full state)
 * - Monotonic version tracking (ignores stale messages)
 * - BroadcastChannel multi-tab sync (single WS per site)
 * - Polling fallback (stops immediately on WS recovery)
 */

import { buildWSUrl, getLiveSnapshot } from './monitoringApi';
import type { WSMessage, WSSnapshotMessage, ActiveAlarmEvent, ProviderHealth } from '@/types/monitoring';

// ============================================
// Types
// ============================================

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error';

export interface StreamCallbacks {
  onSnapshot: (snapshot: WSSnapshotMessage) => void;
  onData: (entities: Array<{ entity_id: string; entity_type: string; metrics: Record<string, number | string | null> }>, timestamp: number) => void;
  onAlarm: (alarm: ActiveAlarmEvent) => void;
  onHealth: (health: ProviderHealth) => void;
  onSchemaUpdated: (schemaHash: string, invalidDatapointIds: string[]) => void;
  onStatusChange: (state: ConnectionState, message?: string) => void;
  onError?: (error: Error) => void;
}

export interface StreamConfig {
  siteId: string;
  pollIntervalMs?: number;
  maxRetries?: number;
  enableBroadcast?: boolean;
}

// ============================================
// Constants
// ============================================

const DEFAULT_POLL_INTERVAL = 10_000;  // 10 seconds (more useful than 60s for live)
const MAX_RETRIES = 10;
const BASE_BACKOFF = 2_000;
const MAX_BACKOFF = 60_000;
const BROADCAST_CHANNEL_NAME = 'soleadlab-monitoring-stream';

// ============================================
// DataStreamManager
// ============================================

export class DataStreamManager {
  private siteId: string;
  private callbacks: StreamCallbacks;
  private config: Required<Omit<StreamConfig, 'siteId'>>;

  // WebSocket state
  private ws: WebSocket | null = null;
  private retryCount = 0;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastSnapshotVersion = 0;

  // Polling state
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private isPollActive = false;

  // Connection state
  private _state: ConnectionState = 'disconnected';
  private destroyed = false;

  // Multi-tab sync
  private broadcastChannel: BroadcastChannel | null = null;

  constructor(config: StreamConfig, callbacks: StreamCallbacks) {
    this.siteId = config.siteId;
    this.callbacks = callbacks;
    this.config = {
      pollIntervalMs: config.pollIntervalMs ?? DEFAULT_POLL_INTERVAL,
      maxRetries: config.maxRetries ?? MAX_RETRIES,
      enableBroadcast: config.enableBroadcast ?? true,
    };

    if (this.config.enableBroadcast) {
      this.initBroadcastChannel();
    }
  }

  get state(): ConnectionState {
    return this._state;
  }

  // ============================================
  // Public API
  // ============================================

  start(): void {
    if (this._state === 'connected' || this._state === 'connecting') return;
    this.destroyed = false;
    this.connectWS();
  }

  stop(): void {
    this.disconnectWS();
    this.stopPolling();
    this.setState('disconnected');
    this.retryCount = 0;
    this.lastSnapshotVersion = 0;

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
  }

  async refresh(): Promise<void> {
    await this.pollOnce();
  }

  switchSite(siteId: string): void {
    this.stop();
    this.siteId = siteId;
    this.start();
  }

  destroy(): void {
    this.destroyed = true;
    this.stop();
    this.broadcastChannel?.close();
    this.broadcastChannel = null;
  }

  // ============================================
  // WebSocket Connection
  // ============================================

  private connectWS(): void {
    if (this.destroyed) return;
    this.setState('connecting');

    try {
      const url = buildWSUrl(this.siteId);
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        this.retryCount = 0;
        this.setState('connected');
        // Stop polling immediately — WS is now active
        this.stopPolling();
      };

      this.ws.onmessage = (event) => {
        this.handleWSMessage(event);
      };

      this.ws.onclose = (event) => {
        this.ws = null;
        if (!this.destroyed) {
          if (event.code === 1008 || event.code === 4001) {
            // Auth rejected — don't retry
            this.setState('error', event.reason || 'Authentication failed');
          } else {
            this.handleWSDisconnect();
          }
        }
      };

      this.ws.onerror = () => {
        // onclose always fires after onerror, so we handle retry there
      };
    } catch (err) {
      console.error('[DataStream] WS connection failed:', err);
      this.handleWSDisconnect();
    }
  }

  private disconnectWS(): void {
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close();
      this.ws = null;
    }
  }

  private handleWSMessage(event: MessageEvent): void {
    try {
      const msg = JSON.parse(event.data) as WSMessage;

      // Version guard: ignore stale messages
      if ('v' in msg && msg.v < this.lastSnapshotVersion) return;

      switch (msg.type) {
        case 'snapshot':
          this.lastSnapshotVersion = msg.v;
          this.callbacks.onSnapshot(msg);
          this.broadcastMessage(msg);
          break;

        case 'data':
          this.callbacks.onData(msg.entities, msg.ts);
          this.broadcastMessage(msg);
          break;

        case 'alarm':
          this.callbacks.onAlarm(msg.alarm);
          this.broadcastMessage(msg);
          break;

        case 'health':
          this.callbacks.onHealth(msg.health);
          this.broadcastMessage(msg);
          break;

        case 'schema_updated':
          this.callbacks.onSchemaUpdated(msg.schemaHash, msg.invalidDatapointIds);
          this.broadcastMessage(msg);
          break;

        case 'error':
          // DO sent a fetch error — surface via onError callback
          console.warn(`[DataStream] Provider error (${msg.kind}): ${msg.message}`);
          this.callbacks.onError?.(new Error(`[${msg.kind}] ${msg.message}`));
          this.broadcastMessage(msg);
          break;
      }
    } catch (err) {
      console.error('[DataStream] WS message parse error:', err);
    }
  }

  private handleWSDisconnect(): void {
    this.ws = null;

    // Start polling as fallback immediately
    if (!this.isPollActive && !this.destroyed) {
      this.startPolling();
    }

    if (this.retryCount < this.config.maxRetries) {
      this.retryCount++;
      const backoff = Math.min(BASE_BACKOFF * Math.pow(2, this.retryCount - 1), MAX_BACKOFF);
      const jitter = Math.random() * backoff * 0.25;
      const delay = backoff + jitter;

      this.setState('reconnecting', `Retry ${this.retryCount}/${this.config.maxRetries} in ${Math.round(delay / 1000)}s`);

      this.retryTimeout = setTimeout(() => {
        this.retryTimeout = null;
        if (!this.destroyed) this.connectWS();
      }, delay);
    } else {
      // Max retries reached — stay on polling
      this.setState('error', 'WebSocket connection failed after retries — using polling');
    }
  }

  // ============================================
  // Polling Fallback
  // ============================================

  private startPolling(): void {
    if (this.isPollActive || this.destroyed) return;
    this.isPollActive = true;
    this.schedulePoll();
  }

  private stopPolling(): void {
    this.isPollActive = false;
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private schedulePoll(): void {
    if (!this.isPollActive || this.destroyed) return;
    this.pollTimer = setTimeout(async () => {
      await this.pollOnce();
      // If WS recovered while we were polling, stop
      if (this._state === 'connected' && this.ws) {
        this.stopPolling();
        return;
      }
      this.schedulePoll();
    }, this.config.pollIntervalMs);
  }

  private async pollOnce(): Promise<void> {
    try {
      const snapshot = await getLiveSnapshot(this.siteId);
      if (snapshot && !this.destroyed) {
        this.callbacks.onSnapshot(snapshot);
      }
    } catch (err) {
      console.error('[DataStream] Poll error:', err);
      this.callbacks.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }

  // ============================================
  // State Management
  // ============================================

  private setState(state: ConnectionState, message?: string): void {
    if (this._state === state) return;
    this._state = state;
    this.callbacks.onStatusChange(state, message);
  }

  // ============================================
  // Multi-Tab Sync (BroadcastChannel)
  // ============================================

  private initBroadcastChannel(): void {
    try {
      this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);

      this.broadcastChannel.onmessage = (event) => {
        const msg = event.data;
        if (!msg || msg.siteId !== this.siteId) return;

        // Only process data from other tabs if we're not the active WS connection
        if (this._state !== 'connected') {
          this.handleWSMessage({ data: JSON.stringify(msg.payload) } as MessageEvent);
        }
      };
    } catch {
      this.broadcastChannel = null;
    }
  }

  private broadcastMessage(msg: WSMessage): void {
    this.broadcastChannel?.postMessage({
      siteId: this.siteId,
      payload: msg,
    });
  }
}

// ============================================
// Factory Function
// ============================================

export function createDataStream(
  config: StreamConfig,
  callbacks: StreamCallbacks
): DataStreamManager {
  return new DataStreamManager(config, callbacks);
}
